
import { isSessionRejected } from './sessionFailure';
import { authService } from './authService';
import { setTokens, clearAllTokens, getRefreshToken } from '../storage/tokenStorage';
import { getTokenExpiresIn } from '../utils/jwt';

/**
 * Token Refresh Logic
 * 
 * Manages access token refresh with smart features:
 * - Prevents concurrent refresh requests (singleton promise)
 * - Queues pending API requests during refresh
 * - Automatically updates token storage
 * - Clears auth only when the server rejects the refresh token
 * 
 * ARCHITECTURE:
 * ```
 * API Request → Check if refresh in progress
 *            ↓
 *            Wait for refresh to complete
 *            ↓
 *            Proceed with new token
 * ```
 */

/**
 * Singleton refresh promise to prevent concurrent refresh requests
 * 
 * When multiple API calls happen simultaneously and all need refresh,
 * they all wait for the same refresh promise instead of making
 * multiple refresh requests.
 */
let refreshPromise: Promise<string> | null = null;

/**
 * Queue of pending requests waiting for token refresh
 * 
 * During token refresh, incoming API requests are paused by adding
 * their resolve functions to this queue. When refresh completes,
 * all queued requests are resumed.
 */
type PendingRequestResolver = () => void;
let pendingRequests: PendingRequestResolver[] = [];

/** Longest a refresh waits for the network to come back before trying anyway. */
export const ONLINE_WAIT_MS = 15_000;

/**
 * Resolve when the browser reports it is online, or after `ONLINE_WAIT_MS`.
 *
 * A laptop waking from sleep fires the refresh watcher before its network is back, and a
 * refresh sent then fails for nothing. Waiting must happen BEFORE the refresh token is
 * read: Supabase rotates it on use, and presenting a stale one more than ~10s after a
 * rotation revokes the whole session in every tab.
 */
const waitForOnline = (): Promise<void> => {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    const timer = setTimeout(done, ONLINE_WAIT_MS);
    window.addEventListener('online', done);
  });
};

/**
 * Refresh access token using stored refresh token
 * 
 * Features:
 * - Singleton pattern: Returns existing promise if refresh in progress
 * - Request queue: Pauses pending requests during refresh
 * - Automatic storage: Updates tokens in storage
 * - Error handling: Clears auth only on a server rejection (401/403)
 * - Offline: waits for the network to return (bounded) before reading the token
 * 
 * @returns New access token
 * @throws Error if refresh fails
 * 
 * @example
 * ```ts
 * try {
 *   const newToken = await refreshAccessToken();
 *   // Token automatically stored, proceed with request
 * } catch (error) {
 *   // Rejected → tokens cleared; transient → session kept for the next attempt
 * }
 * ```
 */
export const refreshAccessToken = async (): Promise<string> => {
  // Return existing promise if refresh already in progress
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      await waitForOnline();

      // Get current refresh token from storage
      const currentRefreshToken = await getRefreshToken();
      
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      // Call refresh API
      const response = await authService.refresh(currentRefreshToken);
      
      // ✅ Supabase automatically rotates refresh tokens
      // response.token is the new access token
      // response.refresh_token is the NEW refresh token (rotated)
      // Note: authService.refresh() already returns result.payload, so we access properties directly
      
      // Extract expiry from the JWT token itself (more reliable than hardcoding)
      const newAccessToken = response.token;
      const newRefreshToken = response.refresh_token;
      const accessExpiresIn = getTokenExpiresIn(newAccessToken);
      
      // Validate we got a valid expiry from the token
      if (!accessExpiresIn || accessExpiresIn <= 0) {
        console.warn('[TokenRefresh] Could not determine token expiry from JWT, using default 3600s');
      }
      
      // Store new access token + NEW refresh token (rotated by Supabase)
      await setTokens({
        accessToken: newAccessToken,
        accessExpiresIn: accessExpiresIn || 3600,  // Use JWT expiry or fallback to 1 hour
        refreshToken: newRefreshToken,  // ✅ Store NEW refresh token
        refreshExpiresIn: 7 * 24 * 60 * 60  // Assume 7 days (backend doesn't specify)
      });
      
      // Resume all pending requests
      if (pendingRequests.length > 0) {
        pendingRequests.forEach(resolve => resolve());
        pendingRequests = [];
      }
      
      return response.token;
      
    } catch (error) {
      console.error('[TokenRefresh] ❌ Token refresh failed:', error);

      // Only wipe the session when the server actually refused the refresh token.
      // A transient failure (offline, timeout, 5xx) leaves it in place so a later
      // attempt can still succeed.
      if (isSessionRejected(error)) {
        await clearAllTokens();
      }
      
      // Release pending requests — they proceed with whatever token is stored and
      // fail on their own if it is no longer good
      if (pendingRequests.length > 0) {
        pendingRequests.forEach(resolve => resolve());
        pendingRequests = [];
      }
      
      throw error;
      
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Add request to pending queue
 * 
 * Used by API interceptor to pause requests during token refresh.
 * Request will be resumed when refresh completes (success or failure).
 * 
 * @returns Promise that resolves when refresh completes
 * 
 * @example
 * ```ts
 * // In API interceptor
 * if (isRefreshInProgress()) {
 *   await queuePendingRequest();
 *   // Now proceed with request using new token
 * }
 * ```
 */
export const queuePendingRequest = (): Promise<void> => {
  return new Promise<void>(resolve => {
    pendingRequests.push(resolve);
  });
};

/**
 * Check if refresh is currently in progress
 * 
 * Used by API interceptor to determine if requests should be queued
 * 
 * @returns True if refresh in progress
 * 
 * @example
 * ```ts
 * if (isRefreshInProgress()) {
 *   await queuePendingRequest();
 * }
 * ```
 */
export const isRefreshInProgress = (): boolean => {
  return refreshPromise !== null;
};

/**
 * Get current queue size
 * 
 * Useful for debugging and monitoring
 * 
 * @returns Number of requests waiting for refresh
 */
export const getPendingRequestCount = (): number => {
  return pendingRequests.length;
};

/**
 * Force clear refresh state
 * 
 * USE WITH CAUTION: Only for error recovery
 * Normally this is handled automatically
 * 
 * @example
 * ```ts
 * // Only use if refresh is stuck
 * forceResetRefreshState();
 * ```
 */
export const forceResetRefreshState = (): void => {
  console.warn('[TokenRefresh] Force resetting refresh state');
  refreshPromise = null;
  pendingRequests.forEach(resolve => resolve());
  pendingRequests = [];
};
